import * as React from 'react'
import { CSSProperties, FunctionComponent, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { Card, List, makeStyles, mergeClasses, shorthands, Skeleton, SkeletonItem, tokens } from '@fluentui/react-components'
import { Formik } from 'formik'
import BaseOverviewFilter, { BaseOverviewFilterProps, BaseOverviewFilterType, ViewType } from './BaseOverviewFilter'
import { BaseOverviewSortingType } from './BaseSortMenu'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import BaseOverviewPage, { BaseOverviewPageProps } from './BaseOverviewPage'
import { EntityDto } from '../../config/appConfig'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import useLocalStorage from '../../hooks/useLocalStorage'
import { parseAttributeToParams } from '../../components/Form/Attributes/AttributesList'
import { BaseOverviewColumn } from './BaseOverviewColumn'
import Loading from '../../components/Loading'
import AuthorizationContext from '../../context/AuthorizationContext'
import ConnectivityContext from '../../context/ConnectivityContext'
import { decodeKanbanDrag, encodeKanbanDrag, HAMMOCK_KANBAN_DRAG_MIME } from './baseOverviewDrag'

export const useOverviewStyles = makeStyles({
    //Move this container up when all pages have been updated.
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingHorizontalS,
    },
    columnContainer: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 900px) minmax(0, 1fr)',
        padding: 0,
        gap: 0,
    },
    columnContainerXXXL: {
        display: 'grid',
        gridTemplateColumns: '1fr',
    },
    multiColumnContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(var(--baseOverviewColumnCount), 1fr)',
        gap: tokens.spacingHorizontalS,

        '> div': {
            width: `var(--baseOverviewColumnWidth)`,
        },
    },
    columnDragOver: {
        outline: `2px dashed ${tokens.colorBrandStroke1}`,
        outlineOffset: '-2px',
        backgroundColor: tokens.colorBrandBackground2,
    },
    cardDraggable: {
        cursor: 'grab',
    },
    filterContainer: {
        display: 'grid',
        gridTemplateColumns: 'auto',
        gridTemplateRows: 'fit-content fit-content',
        gap: tokens.spacingVerticalS,
    },
    filterContainerNarrow: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    filterInput: {
        ...shorthands.padding(0, tokens.spacingHorizontalXS),
    },
    filtersContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    filterTagButton: {
        minWidth: '32px',
        fontWeight: tokens.fontWeightRegular,
    },
    filterTagButtonSelected: {
        backgroundColor: tokens.colorBrandBackground2Pressed,
    },
    listColors: {
        '> div > :not(:last-child)': {
            borderBottom: '1px solid var(--colorNeutralStroke2)',
        },
    },
    list: {
        width: 'min(874px, 100%)',
        alignSelf: 'center',

        '@media screen and (min-width: 930px)': {
            paddingLeft: tokens.spacingHorizontalM,
            paddingRight: tokens.spacingHorizontalM,
            borderLeft: '1px solid var(--colorNeutralStroke2)',
            borderRight: '1px solid var(--colorNeutralStroke2)',
        },
    },
    listXXXL: {
        width: '100%',
        paddingLeft: 'unset',
        paddingRight: 'unset',
        borderLeft: 'unset',
        borderRight: 'unset',
    },
    listPage: {
        minHeight: '800px',
    },
    listItem: {
        display: 'grid',
        gridTemplateColumns: '1fr 5fr 1fr',
        minHeight: '24px',

        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalS,

        '& *[data-hover-actions="visibility"]': {
            visibility: 'hidden',
        },

        '& *[data-hover-actions="display"]': {
            display: 'none',
        },

        '&:hover': {
            backgroundColor: tokens.colorNeutralStroke1Hover,

            '& *[data-hover-actions="visibility"]': {
                visibility: 'visible',
            },

            '& *[data-hover-actions="display"]': {
                display: 'inherit',
            },
        },

        '[data-selected="true"]': {
            // color: tokens.colorNeutralForeground1,
            backgroundColor: tokens.colorBrandStroke2Hover,
            boxShadow: 'inset 0 0 0 1px var(--colorBrandStroke1)',
        },

        '[data-hover-mouse="true"]': {
            cursor: 'pointer',
        },
    },
    listHeaderItem: {
        backgroundColor: tokens.colorNeutralBackground5,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground5,
        },
    },
    listBaseHeaderItem: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
    },
    listBaseCheckboxHeaderItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) auto',
        gridTemplateRows: '1fr',
    },
    listItemEmpty: {
        gridTemplateColumns: 'auto',
    },
    skeletonItem: {
        minHeight: '30px',
    },
    selectionPill: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        flexShrink: 0,
    },
    selectionPillButton: {
        backgroundColor: tokens.colorBrandStroke2Hover,
    },
    gridGapXS: {
        display: 'grid',
        gap: tokens.spacingVerticalXS,
    },
    gridSpan2Columns: {
        gridColumn: 'span 2',
    },
    gridSpan2Rows: {
        gridRow: 'span 2',
    },
    flexGap: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
    },
    flexGapXS: {
        display: 'flex',
        gap: tokens.spacingVerticalXS,
    },
    flexItemGrowOverflow: {
        flexGrow: '1 1 auto',
        minWidth: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    flexGrow: {
        display: 'flex',
        flexGrow: 1,
    },
    flexShrink: {
        display: 'flex',
        flexGrow: 1,
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    },
    flexWrap: {
        flexWrap: 'wrap',
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0!important',
    },
    flexJustifyBetween: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    flexJustifyEnd: {
        display: 'flex',
        justifyContent: 'end',
    },
    flexAlignContentStart: {
        flexWrap: 'wrap',
        alignContent: 'start',
    },
    alignEnd: {
        textAlign: 'end',
    },
    centerItems: {
        alignSelf: 'center',
        textAlign: 'center',
        fontSize: tokens.fontSizeBase300,
    },
    alignItemsCenter: {
        alignItems: 'center',
    },
    alignItemsStart: {
        alignItems: 'start',
    },
    alignItemsEnd: {
        alignItems: 'end',
        textAlign: 'end',
    },
    alignSelfStart: {
        alignSelf: 'start',
    },
    hidden: {
        display: 'none',
    },
    invisible: {
        visibility: 'hidden',
    },
    colorRed: {
        color: tokens.colorStatusDangerForeground3,
    },
    colorOrange: {
        color: tokens.colorStatusWarningBackground3,
    },
    colorGreen: {
        color: tokens.colorStatusSuccessBackground3,
    },
    colorBlue: {
        color: tokens.colorPaletteBlueBorderActive,
    },
    paddingXS: {
        padding: tokens.spacingHorizontalXS,
    },
    overflowHidden: {
        overflow: 'hidden',
    },
    minWidth0: {
        minWidth: '0',
    },
})

/**
 * Basis for overview page props. As little as possible is required.
 */
export type BaseOverviewProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any> = BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DefaultFilterTagsPropsT = any,
> = PropsWithChildren<{
    defaultFilterValues: FilterT
    fixedFilterValues?: FilterT
    fixedColumnFilterValues?: (viewType: ViewType, values: FilterT) => FilterT[]
    batchSizeItems?: number
    maxCount?: number
    countItems: (column?: number, filterValues?: FilterT) => Promise<HeaderT>
    onCountItems?: (column: number, result: HeaderT) => void | Promise<any>
    loadItems: (column: number, filterValues?: FilterT, sortingValues?: SortingT, start?: number, size?: number) => Promise<EntityT[]>
    onLoadItems?: (column: number, columnPages: EntityT[][][], columnItems: EntityT[]) => void | Promise<any>
    alwaysLoadAllItems?: boolean

    listColumnFullWidth?: boolean // When viewing as a list, make the column 100% width
    noSticky?: boolean // Don't sticky the filter bar
    listClassnames?: string[]
    listItemHeaderContent?: FunctionComponent<BaseOverviewHeaderItemProps<EntityT, FilterT, SortingT, HeaderT>>
    listItemHeaderClassnames?: (props: Partial<BaseOverviewLineItemProps<EntityT, FilterT, SortingT>>) => string[]
    listItemContent: (props: BaseOverviewLineItemProps<EntityT, FilterT, SortingT>) => React.ReactNode
    listItemClassnames?: (props: Partial<BaseOverviewLineItemProps<EntityT, FilterT, SortingT>>) => string[]

    headerActionsButtons?: FunctionComponent<BaseOverviewProps<EntityT, FilterT, SortingT> & ActionButtonPropsT>
    headerActionsButtonsProps?: ActionButtonPropsT
    headerDefaultFilterTags?: FunctionComponent<BaseOverviewProps<EntityT, FilterT, SortingT> & DefaultFilterTagsPropsT>
    headerDefaultFilterTagsProps?: DefaultFilterTagsPropsT
    headerSortingItems?: SortingT[]
    filterContent?: FunctionComponent<BaseOverviewFilterProps<EntityT, FilterT, SortingT>>
    // Replaces the item list with an overview specific view (ViewType.CalendarView). It renders inside the
    // filter Formik, so it can read and change the filter values through useFormikContext. While it is shown
    // no items are loaded.
    listOverrideContent?: FunctionComponent<BaseOverviewFilterProps<EntityT, FilterT, SortingT>>
    filterInputHint?: FunctionComponent<BaseOverviewFilterProps<EntityT, FilterT, SortingT>>
    filterFromJson?: (filterValues: FilterT) => FilterT
    filterToJson?: (filterValues: FilterT) => FilterT
    inputPlaceholderKey?: string
    listEmptyPlaceholderKey?: string
    kanbanEmptyPlaceholderKey?: string

    // Kanban-only: invoked when a card is dropped in a different column. When set, cards become
    // draggable in the kanban view. Perform the persisting action (e.g. a status change) and
    // resolve; BaseOverview then reloads both the source and the target column. Rejecting skips
    // the reload so the board keeps reflecting the server state.
    onItemDropInColumn?: (item: EntityT, sourceColumnIndex: number, targetColumnIndex: number, targetColumnFilterValues: FilterT) => Promise<unknown>

    isItemSelectable?: (item: EntityT) => boolean
    isAllItemsSelectable?: (items: EntityT[]) => boolean
    isItemSelected?: (item: EntityT) => boolean
    isAllItemsSelected?: (items: EntityT[]) => boolean
    useItemSelectedColor?: boolean
    onItemSelected?: (data: CheckboxOnChangeData, item: EntityT) => void
    onAllItemsSelected?: (data: CheckboxOnChangeData, items: EntityT[]) => void
    selectedItems?: EntityT[]

    // The click event is passed along so an overview can read modifier keys (Shift+click
    // range selection in the email tab); overviews that only navigate ignore it.
    onItemClicked?: (props: BaseOverviewLineItemProps<EntityT, FilterT, SortingT>, event?: React.MouseEvent) => void
    reloadTrigger?: any
    apiRef?: React.MutableRefObject<{ onUpdatePageItems: (column: number, page: number, start: number, size: number) => Promise<EntityT[]> }>

    localStorageKeyPrefix?: string
    /**
     * Whether the default values this overview hands out are final. An overview whose defaults come from the office
     * settings - the task overviews and their sort date (HAM-4286) - passes false until those settings are in:
     * localStorage snapshots its defaults once, so a default that arrives later would never be used.
     */
    defaultsReady?: boolean

    focusQueryOnRender?: boolean
}>

export type BaseOverviewCount = {
    total: number
}

export type BaseOverviewHeaderItemProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT> & {
    viewportTiny: boolean
    viewportNarrow: boolean
    viewportMedium: boolean
    viewType: ViewType
    columnIndex: number
    filterValues: FilterT
    count?: HeaderT
    items?: EntityT[]

    isItemSelectable?: (item: EntityT) => boolean
    isAllItemsSelectable?: (items: EntityT[]) => boolean
    isItemSelected?: (item: EntityT) => boolean
    isAllItemsSelected?: (items: EntityT[]) => boolean
    onItemSelected?: (data: CheckboxOnChangeData, item: EntityT) => void
    onAllItemsSelected?: (data: CheckboxOnChangeData, items: EntityT[]) => void
    selectedItems?: EntityT[]
}

export type BaseOverviewLineItemProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
> = BaseOverviewPageProps<EntityT, FilterT, SortingT> & {
    index: number
    key: number | string
    item: EntityT
}

export function BaseOverviewSkeletonListItem() {
    const styles = useOverviewStyles()

    return (
        <div className={mergeClasses(styles.listItem, styles.skeletonItem)}>
            <SkeletonItem size={24} shape={'circle'} />
            <SkeletonItem size={24} />
            <SkeletonItem size={24} />
        </div>
    )
}

export function parseFilterValuesToParams<FilterT extends BaseOverviewFilterType, SortingT extends BaseOverviewSortingType<any>>(
    filterValues?: FilterT,
    sortingValues?: SortingT,
    start?: number,
    size?: number,
): any {
    return {
        ...filterValues,
        icon: undefined,
        title: undefined,
        tag: undefined,
        start: start,
        size: size,
        sort: sortingValues && parseSortingValuesToParams(sortingValues),
        attributes: filterValues?.['attributes'] && parseAttributeToParams(filterValues?.['attributes']),
    }
}

export function parseSortingValuesToParams<SortingT extends BaseOverviewSortingType<any>>(sortingValues?: SortingT): string[] {
    if (sortingValues && sortingValues?.property) {
        return sortingValues.property.map(property => `${property.toString()}-${sortingValues?.direction?.toLocaleLowerCase()}`)
    }
    return undefined
}

/**
 * Basis of an overview.
 * Implementing components should implement subtypes of BaseOverviewFilterType and BaseOverviewSortingType.
 * @param props see {@link BaseOverviewProps} for more information on which fields to include.
 */
export default function BaseOverview<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DefaultFilterTagsPropsT = any,
>(props: BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonPropsT, DefaultFilterTagsPropsT>) {
    const styles = useOverviewStyles()
    const { host } = useContext(AuthorizationContext)
    const { online, showOfflineWarning } = useContext(ConnectivityContext)

    const { t: translate } = useTranslation()
    const { viewport, isViewport, width: viewportWidth } = useViewport()

    const viewportTiny = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])
    const viewportNarrow = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookOld), [viewport])
    const viewportMedium = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNewWide), [viewport])
    const viewportXXL = useMemo<boolean>(() => isViewport('>=', Viewport.XXL), [viewport])

    console.debug(
        `viewportTiny: ${viewportTiny}; viewportNarrow: ${viewportNarrow}; viewportMedium: ${viewportMedium}; viewportXXL: ${viewportXXL}; viewport: ${viewportWidth}.`,
    )

    const [filtersOpen, setFiltersOpen] = useState<boolean>(false)

    // Nothing is read from (or fallen back to) storage before the overview knows what its defaults are.
    const storageReady = host !== undefined && props.defaultsReady !== false

    const {
        loaded: loadedViewType,
        values: viewType,
        setValues: setViewType,
    } = useLocalStorage<ViewType>({
        key: props.localStorageKeyPrefix ? `${props.localStorageKeyPrefix}ViewType` : undefined,
        hostKey: host?.RowKey,
        defaultValue: ViewType.ListView,
        fromJson: values => values,
        toJson: values => values,
        load: storageReady,
    })
    const currentViewType: ViewType = useMemo(() => {
        if (!loadedViewType || !viewType) {
            return ViewType.ListView
        }
        // Kanban needs the horizontal space of a wide viewport, the override view works on every viewport but
        // only exists when the overview supplies the content for it.
        if (viewType === ViewType.KanbanView) {
            return viewportXXL ? ViewType.KanbanView : ViewType.ListView
        }
        if (viewType !== ViewType.ListView) {
            return props.listOverrideContent ? viewType : ViewType.ListView
        }
        return viewType
    }, [viewportXXL, loadedViewType, viewType, props.listOverrideContent])
    const listOverridden = currentViewType !== ViewType.ListView && currentViewType !== ViewType.KanbanView
    const {
        loaded: loadedFilterValues,
        values: filterValues,
        setValues: setFilterValues,
    } = useLocalStorage<FilterT>({
        key: props.localStorageKeyPrefix ? `${props.localStorageKeyPrefix}Filter` : undefined,
        hostKey: host?.RowKey,
        defaultValue: { ...props.fixedFilterValues, ...props.defaultFilterValues },
        fromJson: values => {
            return values.query?.length >= 3 ? values : props.filterFromJson?.(values) || values
        },
        toJson: values => {
            return values.query?.length >= 3 ? values : props.filterToJson?.(values) || values
        },
        load: storageReady,
    })
    const {
        loaded: loadedSortingValues,
        values: sortingValues,
        setValues: setSortingValues,
    } = useLocalStorage<SortingT>({
        key: props.localStorageKeyPrefix ? `${props.localStorageKeyPrefix}Sorting` : undefined,
        hostKey: host?.RowKey,
        defaultValue: props.headerSortingItems?.find(item => !!item.default),
        fromJson: values => {
            const property = Array.isArray(values.property) ? values.property : [values.property].filter(Boolean)
            values.property =
                props.headerSortingItems.find(item => property?.[0] === item.property[0] && item.direction === values.direction)?.property ||
                props.headerSortingItems.find(item => item.default)?.property ||
                property
            return values
        },
        toJson: values => values,
        load: storageReady,
    })

    const fixedColumnFilterValues: FilterT[] = useMemo(
        () => (loadedFilterValues && props.fixedColumnFilterValues?.(currentViewType, filterValues)) || ([{}] as FilterT[]),
        [loadedFilterValues, currentViewType, filterValues],
    )

    const [initialized, setInitialized] = useState<boolean>(false)
    const batchSizeItems: number = useMemo(() => props.batchSizeItems ?? 20, [[props.batchSizeItems]])
    const [count, setCount] = useState<HeaderT[]>(fixedColumnFilterValues.map(() => ({ total: 0 }) as HeaderT))
    const pages: number[] = useMemo(
        () => count?.map(countItem => Math.ceil((countItem?.total ?? 0) / batchSizeItems)) ?? fixedColumnFilterValues.map(() => 0),
        [count, batchSizeItems],
    )

    const [items, setItems] = useState<EntityT[][][]>([])
    const [loading, setLoading] = useState<boolean>(true)

    // Card drag-and-drop between kanban columns. Only active when a drop action is supplied and
    // we're rendering the kanban board. `dragOverColumnIndex` drives the drop-target highlight.
    const dragActive: boolean = !!props.onItemDropInColumn && currentViewType === ViewType.KanbanView
    const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | undefined>(undefined)

    const onUpdateCount = (columns?: number[]): Promise<void> => {
        const hasColumns = columns && columns.length > 0
        //Reset count
        if (!hasColumns) {
            setCount(fixedColumnFilterValues.map(() => ({ total: 0 }) as HeaderT))
        } else {
            const newCount = count ?? fixedColumnFilterValues.map(() => ({ total: 0 }) as HeaderT)
            columns.forEach(column => {
                newCount[column] = { total: 0 } as HeaderT
            })
            setCount([...newCount])
        }

        //Invoke recount
        return Promise.all(
            fixedColumnFilterValues.map((columnFilters, columnIndex) => {
                if (!hasColumns || columns.includes(columnIndex)) {
                    return props
                        .countItems(columnIndex, { ...filterValues, ...columnFilters })
                        .then(
                            response =>
                                ({ ...response, total: props.maxCount ? Math.min(props.maxCount, response?.total) : response?.total }) as HeaderT,
                        )
                        .catch(() => {
                            console.debug('BaseOverview', 'Encountered an error when fetching count of column', columnIndex)
                            return { total: 0 } as HeaderT
                        })
                        .then(result => {
                            const newCount = count ?? (Array(fixedColumnFilterValues.length) as HeaderT[])
                            newCount[columnIndex] = result
                            setCount([...newCount])

                            const newItems = items ?? (Array(fixedColumnFilterValues.length) as EntityT[][][])
                            newItems[columnIndex] = []
                            setItems([...newItems])

                            props.onCountItems?.(columnIndex, result)
                        })
                }
                return Promise.resolve()
            }),
        ).then(() => {})
    }

    const onUpdatePageItems = (column: number, page: number, start: number, size: number): Promise<EntityT[]> => {
        if (!start) {
            start = 0
        }
        if (!page) {
            page = (start - (start % batchSizeItems)) / batchSizeItems
        }
        if (!size) {
            size = batchSizeItems
        }

        return props
            .loadItems(column, { ...filterValues, ...fixedColumnFilterValues[column] }, sortingValues, start, size)
            .catch(() => {
                console.debug('BaseOverview', 'Encountered an error when fetching items of column', column, 'of page', page)
                return []
            })
            .then(result => {
                const newItems = items
                newItems[column][page] = result
                return setItems([...newItems])
            })
            .then(() => {
                const allItems = items
                    .reduce((acc, item) => acc.concat(item), [])
                    .reduce((acc, item) => acc.concat(item), [])
                    .filter(Boolean)
                props.onLoadItems?.(column, items, allItems)
                return allItems
            })
    }

    const onCardDragStart = (event: React.DragEvent, item: EntityT, sourceColumnIndex: number) => {
        event.dataTransfer.setData(HAMMOCK_KANBAN_DRAG_MIME, encodeKanbanDrag({ itemId: item.id, sourceColumnIndex }))
        event.dataTransfer.effectAllowed = 'move'
    }

    const onColumnDragOver = (event: React.DragEvent, targetColumnIndex: number) => {
        // Only intercept our own card drags; leaving OS file drags alone lets the Dropzone handle them.
        if (!event.dataTransfer.types.includes(HAMMOCK_KANBAN_DRAG_MIME)) {
            return
        }
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        if (dragOverColumnIndex !== targetColumnIndex) {
            setDragOverColumnIndex(targetColumnIndex)
        }
    }

    const onColumnDrop = (event: React.DragEvent, targetColumnIndex: number) => {
        const payload = decodeKanbanDrag(event.dataTransfer)
        setDragOverColumnIndex(undefined)
        if (!payload || payload.sourceColumnIndex === targetColumnIndex) {
            return
        }
        event.preventDefault()

        const item = (items[payload.sourceColumnIndex] ?? [])
            .reduce((acc, page) => acc.concat(page), [] as EntityT[])
            .find(candidate => candidate?.id === payload.itemId)
        if (!item) {
            return
        }

        props
            .onItemDropInColumn?.(item, payload.sourceColumnIndex, targetColumnIndex, fixedColumnFilterValues[targetColumnIndex])
            .then(() => onUpdateCount([payload.sourceColumnIndex, targetColumnIndex]))
            .catch(() => console.debug('BaseOverview', 'Drop action failed for item', payload.itemId))
    }

    const onCardDragEnd = () => setDragOverColumnIndex(undefined)

    useEffect(() => {
        if (listOverridden) {
            // The list isn't rendered, so don't spend a request on it. Leave `loading` alone: turning it off
            // here would render the list with empty items for one frame when the user switches back.
            return
        }

        if (!initialized || online) {
            setLoading(true)
            if (loadedViewType && loadedFilterValues && loadedSortingValues) {
                onUpdateCount().then(() => {
                    setLoading(false)
                    setInitialized(true)
                })
            }
        }
    }, [currentViewType, listOverridden, filterValues, sortingValues, online, props.reloadTrigger])

    if (props.apiRef) {
        props.apiRef.current = { onUpdatePageItems }
    }

    if (!loadedViewType || !loadedFilterValues || !loadedSortingValues) {
        return <Loading />
    }

    // The header row is not an item, but both classname callbacks are handed the same shape as a real row so an
    // overview can size its columns from one place. Index -2 marks it as the header.
    const headerLineItemProps = (columnIndex: number): Partial<BaseOverviewLineItemProps<EntityT, FilterT, SortingT>> => ({
        ...props,
        viewportTiny,
        viewportNarrow,
        viewportMedium,
        viewType: currentViewType,
        index: -2,
        key: `${columnIndex}--2`,
    })

    return (
        <div
            className={styles.pageContainer}
            style={
                {
                    '--baseOverviewColumnCount': `${fixedColumnFilterValues.length || 1}`,
                    '--baseOverviewColumnWidth': `${1 / (fixedColumnFilterValues.length || 1)}fr`,
                    '--baseOverviewCheckboxWidth': `28px`,
                    '--baseOverviewAvatarWidth': `24px`,
                    '--baseOverviewIconWidth': `24px`,
                    '--baseOverviewDoubleIconWidth': `56px`,
                    '--baseOverviewDateWidth': `70px`,
                    '--baseOverviewTimeWidth': `48px`,
                    '--baseOverviewFileWidth': `80px`,
                    '--baseOverviewPriceWidth': `90px`,
                    '--baseOverviewPhoneWidth': `120px`,
                } as CSSProperties
            }
        >
            <Formik<FilterT> initialValues={filterValues} validateOnChange onSubmit={setFilterValues} validate={setFilterValues}>
                <>
                    {/* The bar carries the search box, the sort menu and the action buttons, so an overview
                        without filter fields (the notes of a dossier) still needs it. */}
                    {(props.filterContent || props.headerActionsButtons) && (
                        <BaseOverviewFilter<EntityT, FilterT, SortingT>
                            {...props}
                            filtersOpen={filtersOpen}
                            setFiltersOpen={(value?: boolean) =>
                                //Use falsy check for booleans, otherwise 'false' will result in a 'true' through '!oldValue'.
                                setFiltersOpen(oldValue => value ?? !oldValue)
                            }
                            sortingValues={sortingValues}
                            setSortingValues={setSortingValues}
                            viewportTiny={viewportTiny}
                            viewportNarrow={viewportNarrow}
                            viewportMedium={viewportMedium}
                            viewType={currentViewType}
                            setViewType={setViewType}
                        >
                            {props.filterContent && (
                                <props.filterContent
                                    {...props}
                                    filtersOpen={filtersOpen}
                                    setFiltersOpen={setFiltersOpen}
                                    sortingValues={sortingValues}
                                    setSortingValues={setSortingValues}
                                    viewportTiny={viewportTiny}
                                    viewportNarrow={viewportNarrow}
                                    viewportMedium={viewportMedium}
                                    viewType={currentViewType}
                                    setViewType={setViewType}
                                />
                            )}
                        </BaseOverviewFilter>
                    )}
                    {listOverridden && props.listOverrideContent && (
                        <props.listOverrideContent
                            {...props}
                            filtersOpen={filtersOpen}
                            setFiltersOpen={setFiltersOpen}
                            sortingValues={sortingValues}
                            setSortingValues={setSortingValues}
                            viewportTiny={viewportTiny}
                            viewportNarrow={viewportNarrow}
                            viewportMedium={viewportMedium}
                            viewType={currentViewType}
                            setViewType={setViewType}
                        />
                    )}
                </>
            </Formik>

            {props.children}

            {!listOverridden && loading && (
                <Skeleton
                    className={mergeClasses(
                        ...[
                            styles.listColors,
                            currentViewType === ViewType.KanbanView && styles.listXXXL,
                            ...(props.listClassnames || [styles.list]),
                        ],
                    )}
                >
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                    <BaseOverviewSkeletonListItem />
                </Skeleton>
            )}
            {!listOverridden && !loading && currentViewType && (
                <Card
                    className={mergeClasses(
                        styles.columnContainer,
                        props.listColumnFullWidth && styles.columnContainerXXXL,
                        currentViewType === ViewType.KanbanView && styles.multiColumnContainer,
                    )}
                >
                    {fixedColumnFilterValues.length === 0 && (
                        <Card className={mergeClasses(styles.flexGrow, styles.flexColumn)}>
                            <ul>
                                <li
                                    key={`0--4`}
                                    className={mergeClasses(
                                        ...[
                                            styles.listItem,
                                            ...(props.listItemClassnames?.({
                                                ...props,
                                                viewportTiny: viewportTiny,
                                                viewportNarrow: viewportNarrow,
                                                viewportMedium: viewportMedium,
                                                viewType: currentViewType,
                                                index: -4,
                                                key: `0--4`,
                                            }) || []),
                                            styles.listItemEmpty,
                                        ],
                                    )}
                                >
                                    <div>{translate(props.kanbanEmptyPlaceholderKey ?? 'base.empty.filter')}</div>
                                </li>
                            </ul>
                        </Card>
                    )}
                    {fixedColumnFilterValues.length === 1 && <div></div>}
                    {fixedColumnFilterValues.map((columnFilterValues, columnIndex) => (
                        <BaseOverviewColumn
                            key={columnIndex}
                            {...props}
                            columnFilterValues={columnFilterValues}
                            columnIndex={columnIndex}
                            viewportTiny={viewportTiny}
                            viewportNarrow={viewportNarrow}
                            viewportMedium={viewportMedium}
                            viewType={currentViewType}
                            dragActive={dragActive}
                            isDragOver={dragActive && dragOverColumnIndex === columnIndex}
                            onColumnDragOver={onColumnDragOver}
                            onColumnDrop={onColumnDrop}
                            onColumnDragLeave={() => setDragOverColumnIndex(undefined)}
                        >
                            <List
                                className={mergeClasses(
                                    ...[
                                        styles.listColors,
                                        !props.listColumnFullWidth && styles.list,
                                        currentViewType === ViewType.KanbanView && styles.listXXXL,
                                        ...(props.listClassnames || []),
                                    ],
                                )}
                            >
                                {count[columnIndex] && props.listItemHeaderContent && (
                                    <li
                                        key={`${columnIndex}--2`}
                                        className={mergeClasses(
                                            ...[
                                                styles.listItem,
                                                styles.alignItemsCenter,
                                                ...(props.listItemClassnames?.(headerLineItemProps(columnIndex)) || []),
                                                styles.listHeaderItem,
                                                ...(props.listItemHeaderClassnames?.(headerLineItemProps(columnIndex)) || []),
                                            ],
                                        )}
                                    >
                                        <props.listItemHeaderContent
                                            key={-2}
                                            {...props}
                                            viewportTiny={viewportTiny}
                                            viewportNarrow={viewportNarrow}
                                            viewportMedium={viewportMedium}
                                            viewType={currentViewType}
                                            columnIndex={columnIndex}
                                            filterValues={filterValues}
                                            count={count[columnIndex]}
                                            items={(items[columnIndex] ?? []).reduce((acc, item) => acc.concat(item), []).filter(Boolean)}
                                        />
                                    </li>
                                )}
                                {count[columnIndex] && count[columnIndex].total === 0 && (
                                    <li
                                        key={`${columnIndex}--3`}
                                        className={mergeClasses(
                                            ...[
                                                styles.listItem,
                                                styles.alignItemsCenter,
                                                ...(props.listItemClassnames?.({
                                                    ...props,
                                                    viewportTiny: viewportTiny,
                                                    viewportNarrow: viewportNarrow,
                                                    viewportMedium: viewportMedium,
                                                    viewType: currentViewType,
                                                    index: -3,
                                                    key: `${columnIndex}--3`,
                                                }) || []),
                                                styles.listItemEmpty,
                                            ],
                                        )}
                                    >
                                        <div>
                                            {translate(
                                                props.listEmptyPlaceholderKey ?? (showOfflineWarning ? 'base.overview.empty.offline' : 'base.empty'),
                                            )}
                                        </div>
                                    </li>
                                )}
                                {[...Array(pages[columnIndex]).keys()].map((pageIndex, index) => (
                                    <BaseOverviewPage
                                        key={`${columnIndex}-${index}`}
                                        {...props}
                                        viewportTiny={viewportTiny}
                                        viewportNarrow={viewportNarrow}
                                        viewportMedium={viewportMedium}
                                        viewType={currentViewType}
                                        filterValues={filterValues}
                                        sortingValues={sortingValues}
                                        count={count[columnIndex]}
                                        batchSizeItems={batchSizeItems}
                                        columnIndex={columnIndex}
                                        pageIndex={pageIndex}
                                        items={items[columnIndex]}
                                        onUpdateItem={(_item, columns) => onUpdateCount(columns)}
                                        onUpdatePageItems={onUpdatePageItems}
                                        dragActive={dragActive}
                                        onCardDragStart={onCardDragStart}
                                        onCardDragEnd={onCardDragEnd}
                                    />
                                ))}
                            </List>
                        </BaseOverviewColumn>
                    ))}
                    {fixedColumnFilterValues.length === 1 && <div></div>}
                </Card>
            )}
        </div>
    )
}
